<script setup lang="ts">
import DimensionUnit = App.Enums.DimensionUnits;
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
	unitValues,
	getUnitCategory,
	getAvailableUnits,
	customTrunc,
} from "@/modules/dimension_values/components/BucketUnits";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const INPUT_MIN = -1_000_000_000;
const INPUT_MAX = 1_000_000_000;

const props = withDefaults(
	defineProps<{
		unit: DimensionUnit;
		size?: "compact" | "default";
		min?: number;
		max?: number;
		disabled?: boolean;
	}>(),
	{
		size: "default",
		min: INPUT_MIN,
		max: INPUT_MAX,
		disabled: false,
	}
);

const internalValue = defineModel<number>("value", { required: true });

const displayValues = ref<Record<DimensionUnit, string>>(
	{} as Record<DimensionUnit, string>
);

// Some values start at a higher unit (day instead of minute)
// So, we multiply it by the scale of its starting unit (day: 1 => 1440)
const adjustedValue = computed(() => {
	return unitValues[props.unit].scale;
});

function convertToDisplayValue(selectedUnit: DimensionUnit): number {
	const selectedUnitValue = unitValues[selectedUnit];
	return customTrunc(
		((internalValue.value * adjustedValue.value) / selectedUnitValue.scale) %
			selectedUnitValue.modulo
	);
}

function convertToDisplayValues() {
	const availableUnits = getAvailableUnits(props.unit);
	if (!availableUnits) return;

	const temp = {} as Record<DimensionUnit, string>;
	for (const selectedUnit of availableUnits) {
		temp[selectedUnit] = convertToDisplayValue(selectedUnit)
			.toString()
			.padStart(unitValues[selectedUnit].paddingSize, "0");
	}
	displayValues.value = temp;
}

function convertToInternalValue() {
	const val = Object.entries(displayValues.value).reduce(
		(acc, [DimensionUnit, value]) => {
			if (typeof value === "string" && (!value || value.trim() === "")) {
				return NaN;
			}
			const newValue =
				(Number(value) * unitValues[DimensionUnit].scale) / adjustedValue.value;
			return acc + newValue;
		},
		0
	);
	const clamped = Math.max(Math.min(props.max, val), props.min);

	if (isNaN(clamped) || clamped !== val) {
		// Invalid or out of bounds input -> rerender values
		const temp = isNaN(clamped) ? internalValue.value : clamped;
		internalValue.value = NaN;
		nextTick(() => (internalValue.value = temp));
	} else if (internalValue.value !== clamped) {
		internalValue.value = clamped;
	}
}

const unitCategory = computed(() => {
	return getUnitCategory(props.unit);
});

onMounted(() => convertToDisplayValues());
watch(internalValue, () => convertToDisplayValues());
</script>

<template>
	<div
		class="d-inline-flex align-center"
		:style="`gap: ${unitCategory === 'money' ? 2 : 4}px`"
	>
		<div v-for="(_, DimensionUnit) in displayValues" :key="DimensionUnit">
			<span v-if="unitCategory === 'money'" class="suffix-format">
				{{ t(`units.${DimensionUnit}.short`) }}
			</span>

			<input
				v-model.lazy.number="displayValues[DimensionUnit]"
				:disabled
				:class="[
					'unit-input',
					props.size === 'compact'
						? 'unit-input-compact'
						: 'unit-input-comfortable',
				]"
				:size="
					Math.max(2, displayValues[DimensionUnit]?.toString().length ?? 2) + 1
				"
				@blur="convertToInternalValue"
				@keyup.enter="convertToInternalValue"
			/>

			<span v-if="unitCategory !== 'money'" class="suffix-format">
				{{ t(`units.${DimensionUnit}.short`) }}
			</span>
		</div>
	</div>
</template>

<style scoped>
.unit-input {
	padding-right: 3px;
	margin: 1px;
	border: 1px solid rgb(var(--color-secondary));
	background-color: rgb(var(--color-background));
	border-radius: 4px;
	text-align: right;
}

.unit-input:disabled {
	color: rgba(var(--color-on-surface), 0.5);
}

.unit-input-comfortable {
	height: 24px;
	font-size: 16px;
}
.unit-input-compact {
	height: 20px;
	font-size: 14px;
}

.suffix-format {
	color: #555;
	font-size: 0.8rem;
	font-weight: 400;
	line-height: 1.25rem;
}
</style>
